/**
 * BoardSelector Component
 *
 * Dropdown selector for choosing a target board in CallBoardNode.
 * Fetches available boards from the API and displays them in a
 * searchable dropdown.
 *
 * Features:
 * - Lazy loading: Only fetches boards when dropdown is opened
 * - Search/filter: Type to filter boards by name
 * - Loading state: Shows spinner while fetching
 * - Empty state: Message when no boards available
 */

import { useState, useEffect, useRef, useCallback } from 'react';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Board data from API.
 */
interface Board {
  id: string;
  name: string;
  description?: string;
}

/**
 * Props for BoardSelector component.
 */
interface BoardSelectorProps {
  /** Currently selected board ID */
  selectedBoardId?: string;

  /** Callback when a board is selected */
  onSelect: (boardId: string, boardName: string) => void;

  /** Optional placeholder text */
  placeholder?: string;

  /** Whether the selector is disabled */
  disabled?: boolean;
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * BoardSelector - Dropdown for selecting a target board.
 *
 * Fetches boards from /api/boards when opened and lets the user
 * select one. The selected board will be executed when CallBoardNode runs.
 *
 * @example
 * <BoardSelector
 *   selectedBoardId={data.targetBoardId}
 *   onSelect={(id, name) => {
 *     onTargetBoardChange?.(id, name);
 *   }}
 * />
 */
export function BoardSelector({
  selectedBoardId,
  onSelect,
  placeholder = 'Select a board...',
  disabled = false,
}: BoardSelectorProps) {
  // ========================================
  // State
  // ========================================

  // List of available boards from API
  const [boards, setBoards] = useState<Board[]>([]);

  // Loading state for API fetch
  const [loading, setLoading] = useState(false);

  // Error state
  const [error, setError] = useState<string | null>(null);

  // Whether dropdown is open
  const [isOpen, setIsOpen] = useState(false);

  // Search/filter text
  const [searchText, setSearchText] = useState('');

  // Ref for click-outside detection
  const containerRef = useRef<HTMLDivElement>(null);

  // ========================================
  // Fetch Boards
  // ========================================

  /**
   * Fetch boards from API when dropdown opens.
   * Only fetches once, caches the result.
   */
  useEffect(() => {
    if (isOpen && boards.length === 0 && !loading && !error) {
      setLoading(true);

      fetch('/api/boards')
        .then((response) => {
          if (!response.ok) {
            throw new Error('Failed to fetch boards');
          }
          return response.json();
        })
        .then((data) => {
          // API returns { boards: Board[] }
          const boardList = data.boards || data || [];
          setBoards(boardList);
          setError(null);
        })
        .catch((err) => {
          console.error('[BoardSelector] Failed to fetch boards:', err);
          setError('Failed to load boards');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [isOpen, boards.length, loading, error]);

  // ========================================
  // Click Outside Handler
  // ========================================

  /**
   * Close dropdown when clicking outside.
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchText('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // ========================================
  // Handlers
  // ========================================

  /**
   * Toggle dropdown open/closed.
   */
  const toggleDropdown = useCallback(() => {
    if (!disabled) {
      setIsOpen((prev) => !prev);
      if (!isOpen) {
        setSearchText('');
      }
    }
  }, [disabled, isOpen]);

  /**
   * Handle board selection.
   */
  const handleSelect = useCallback(
    (board: Board) => {
      onSelect(board.id, board.name);
      setIsOpen(false);
      setSearchText('');
    },
    [onSelect]
  );

  // ========================================
  // Derived State
  // ========================================

  // Find currently selected board for display
  const selectedBoard = boards.find((b) => b.id === selectedBoardId);

  // Filter boards by search text
  const filteredBoards = boards.filter((board) =>
    board.name.toLowerCase().includes(searchText.toLowerCase())
  );

  // ========================================
  // Render
  // ========================================

  return (
    <div className="relative" ref={containerRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={toggleDropdown}
        disabled={disabled}
        className={`
          w-full px-3 py-2 text-left text-sm
          bg-gray-50 border border-gray-200 rounded-lg
          hover:border-indigo-300 transition-colors
          flex items-center justify-between
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
      >
        <span className={selectedBoard ? 'text-gray-900' : 'text-gray-400'}>
          {selectedBoard ? selectedBoard.name : placeholder}
        </span>

        {/* Dropdown arrow */}
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-100">
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search boards..."
              className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:border-indigo-300"
              autoFocus
            />
          </div>

          {/* Board List */}
          <div className="max-h-48 overflow-y-auto">
            {/* Loading State */}
            {loading && (
              <div className="p-3 text-center text-gray-500 text-sm">
                <svg
                  className="animate-spin h-4 w-4 mx-auto mb-1"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                Loading boards...
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="p-3 text-center text-red-500 text-sm">
                {error}
                <button
                  onClick={() => {
                    setError(null);
                    setBoards([]);
                  }}
                  className="block mx-auto mt-1 text-indigo-600 hover:underline"
                >
                  Retry
                </button>
              </div>
            )}

            {/* Empty State */}
            {!loading && !error && filteredBoards.length === 0 && (
              <div className="p-3 text-center text-gray-500 text-sm">
                {searchText
                  ? 'No boards match your search'
                  : 'No boards available'}
              </div>
            )}

            {/* Board Options */}
            {!loading &&
              !error &&
              filteredBoards.map((board) => (
                <button
                  key={board.id}
                  onClick={() => handleSelect(board)}
                  className={`
                    w-full px-3 py-2 text-left text-sm
                    hover:bg-indigo-50 transition-colors
                    flex flex-col
                    ${
                      board.id === selectedBoardId
                        ? 'bg-indigo-100 text-indigo-700'
                        : ''
                    }
                  `}
                >
                  <span className="font-medium">{board.name}</span>
                  {board.description && (
                    <span className="text-xs text-gray-500 truncate">
                      {board.description}
                    </span>
                  )}
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
